/**
 * Serviço de cache para imagens (avatares)
 * Armazena o conteúdo das imagens como Blob para evitar requisições repetidas
 */

interface CachedImage {
  blobUrl: string;
  timestamp: number;
  expiresAt: number;
  originalUrl: string;
}

class ImageCacheService {
  private readonly CACHE_KEY_PREFIX = 'image_cache_';
  private readonly CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 dias em ms
  private readonly MAX_CACHE_SIZE = 50; // Máximo 50 imagens em cache
  private blobUrlCache = new Map<string, string>(); // Cache em memória para blob URLs

  /**
   * Obtém imagem do cache se ainda válida
   * @param userId ID do usuário
   * @returns URL do blob ou null se não encontrado/expirado
   */
  getCachedImage(userId: string): string | null {
    try {
      // Verificar cache em memória primeiro
      if (this.blobUrlCache.has(userId)) {
        return this.blobUrlCache.get(userId)!;
      }

      const cacheKey = `${this.CACHE_KEY_PREFIX}${userId}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;

      const imageData: CachedImage = JSON.parse(cached);
      const now = Date.now();

      // Verificar se o cache ainda é válido
      if (now > imageData.expiresAt) {
        this.removeCachedImage(userId);
        return null;
      }

      // Verificar se o blob URL ainda é válido
      if (this.isBlobUrlValid(imageData.blobUrl)) {
        // Adicionar ao cache em memória
        this.blobUrlCache.set(userId, imageData.blobUrl);
        return imageData.blobUrl;
      } else {
        // Blob URL inválido, remover do cache
        this.removeCachedImage(userId);
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  /**
   * Armazena imagem no cache
   * @param userId ID do usuário
   * @param imageUrl URL original da imagem
   * @param blob Blob da imagem
   */
  async setCachedImage(userId: string, imageUrl: string, blob: Blob): Promise<string> {
    try {
      // Criar blob URL
      const blobUrl = URL.createObjectURL(blob);
      
      // Adicionar ao cache em memória
      this.blobUrlCache.set(userId, blobUrl);

      // Verificar limite de cache
      await this.cleanupOldCache();

      const cacheKey = `${this.CACHE_KEY_PREFIX}${userId}`;
      const now = Date.now();
      
      const imageData: CachedImage = {
        blobUrl,
        timestamp: now,
        expiresAt: now + this.CACHE_DURATION,
        originalUrl: imageUrl
      };

      localStorage.setItem(cacheKey, JSON.stringify(imageData));
      return blobUrl;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Baixa imagem e armazena no cache
   * @param userId ID do usuário
   * @param imageUrl URL da imagem
   * @returns URL do blob
   */
  async downloadAndCacheImage(userId: string, imageUrl: string): Promise<string> {
    try {
      // Verificar se já está em cache
      const cached = this.getCachedImage(userId);
      if (cached) {
        return cached;
      }

      // Baixar imagem
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Erro ao baixar imagem: ${response.status}`);
      }

      const blob = await response.blob();
      
      // Armazenar no cache
      return await this.setCachedImage(userId, imageUrl, blob);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Armazena uma blob URL no cache (para uso com a nova API)
   * @param userId ID do usuário
   * @param blobUrl URL do blob
   */
  cacheImage(userId: string, blobUrl: string): void {
    try {
      // Adicionar ao cache em memória
      this.blobUrlCache.set(userId, blobUrl);

      // Verificar limite de cache
      this.cleanupOldCache();

      const cacheKey = `${this.CACHE_KEY_PREFIX}${userId}`;
      const now = Date.now();
      
      const imageData: CachedImage = {
        blobUrl,
        timestamp: now,
        expiresAt: now + this.CACHE_DURATION,
        originalUrl: blobUrl // Para blob URLs, usar a própria URL como original
      };

      localStorage.setItem(cacheKey, JSON.stringify(imageData));
    } catch (error) {
      // Erro ao cachear imagem, continuar
    }
  }

  /**
   * Remove imagem do cache
   * @param userId ID do usuário
   */
  removeCachedImage(userId: string): void {
    try {
      // Remover do cache em memória
      const blobUrl = this.blobUrlCache.get(userId);
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        this.blobUrlCache.delete(userId);
      }

      // Remover do localStorage
      const cacheKey = `${this.CACHE_KEY_PREFIX}${userId}`;
      localStorage.removeItem(cacheKey);
    } catch (error) {
      // Erro ao remover cache de imagem, continuar
    }
  }

  /**
   * Verifica se um blob URL ainda é válido
   * @param blobUrl URL do blob
   * @returns true se válido
   */
  private isBlobUrlValid(blobUrl: string): boolean {
    try {
      // Tentar criar um elemento de teste
      const testImg = new Image();
      return blobUrl.startsWith('blob:');
    } catch {
      return false;
    }
  }

  /**
   * Limpa caches expirados e mantém limite de tamanho
   */
  private async cleanupOldCache(): Promise<void> {
    try {
      const now = Date.now();
      const keys = Object.keys(localStorage);
      const imageKeys = keys.filter(key => key.startsWith(this.CACHE_KEY_PREFIX));
      
      // Remover expirados
      const validImages: Array<{ key: string; timestamp: number }> = [];
      
      for (const key of imageKeys) {
        const cached = localStorage.getItem(key);
        if (cached) {
          try {
            const imageData: CachedImage = JSON.parse(cached);
            if (now <= imageData.expiresAt) {
              validImages.push({ key, timestamp: imageData.timestamp });
            } else {
              // Expirado, remover
              const userId = key.replace(this.CACHE_KEY_PREFIX, '');
              this.removeCachedImage(userId);
            }
          } catch (error) {
            // Dados corrompidos, remover
            localStorage.removeItem(key);
          }
        }
      }

      // Se excedeu o limite, remover os mais antigos
      if (validImages.length > this.MAX_CACHE_SIZE) {
        validImages.sort((a, b) => a.timestamp - b.timestamp);
        const toRemove = validImages.slice(0, validImages.length - this.MAX_CACHE_SIZE);
        
        for (const { key } of toRemove) {
          const userId = key.replace(this.CACHE_KEY_PREFIX, '');
          this.removeCachedImage(userId);
        }
      }
    } catch (error) {
      // Erro ao limpar cache antigo, continuar
    }
  }

  /**
   * Limpa todo o cache de imagens
   */
  clearAllImages(): void {
    try {
      // Limpar cache em memória
      for (const blobUrl of Array.from(this.blobUrlCache.values())) {
        URL.revokeObjectURL(blobUrl);
      }
      this.blobUrlCache.clear();

      // Limpar localStorage
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      // Erro ao limpar todo o cache, continuar
    }
  }

  /**
   * Obtém estatísticas do cache
   */
  getCacheStats(): { totalImages: number; memoryCache: number } {
    const keys = Object.keys(localStorage);
    const imageKeys = keys.filter(key => key.startsWith(this.CACHE_KEY_PREFIX));
    
    return {
      totalImages: imageKeys.length,
      memoryCache: this.blobUrlCache.size
    };
  }
}

export const imageCacheService = new ImageCacheService();
